<template>
  <div class="multi-container">
    <div v-if="!isAnyIframeVisible" class="gb-pd">
      <div class="gray-box">
        <p>{{ t('multi_screen_guide') }}</p>
      </div>
    </div>
    <div class="container-grid">
      <div v-for="(itemIframe, indexIframe) in mapIframe" :key="indexIframe" class="iframe-container">
        <div
          v-if="!itemIframe.showIframe"
          :style="{
            paddingTop: '24px',
            paddingLeft: '24px',
            paddingRight: isRightIframeVisible(indexIframe) ? '24px' : '0',
          }"
        >
          <!-- <input type="text" v-model="itemIframe.src" @keydown.enter="onClickMove(+indexIframe)" /> -->
          <div class="language-dropdown">
            <div class="lang-select-btn xl-btn flex-btw" @click="toggleDropdown(indexIframe)">
              <p class="bs">
                {{ itemIframe.selectedLanguage.name || t('select_language') }}
              </p>
              <IconArrow class="icon" />
            </div>
            <div v-if="itemIframe.showDropdown" class="language-options">
              <LanguageList
                :languages="languageStore.getInterpretationLanguageOptions('SCREEN')"
                show-all-in-list
                selected-at-top
                @select="changeLanguage($event, indexIframe)"
              />
            </div>
          </div>

          <div class="btn-pst mt-4">
            <ButtonProcess
              :disabled="!itemIframe.src || isEmpty(itemIframe.selectedLanguage)"
              full-width
              @click="onClickMove(+indexIframe)"
            >
              {{ t('screen_enter') }}
            </ButtonProcess>
          </div>
        </div>
        <iframe
          v-else
          ref="refsIframe"
          :src="itemIframe.src"
          class="iframe-screen"
          :tabindex="+indexIframe + 1"
        ></iframe>
      </div>
    </div>
    <div v-if="isVisibleButtonBlock" class="block-button">
      <button class="minus" :disabled="isDisabledButtonDelete" @click="iframeDelete">
        <img :src="getAssetImageUrl('common/icons/delect-minus.svg')" alt="" />
      </button>
      <button class="plus" :disabled="isDisabledButtonAdd" @click="iframeAdd">
        <img :src="getAssetImageUrl('common/icons/add-plus.svg')" alt="" />
      </button>
      <button class="all-btn" @click="handleFinish">{{ t('enter_all_screens') }}</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router';

import { isEmpty } from 'lodash';

import IconArrow from '@/components/icons/IconArrow.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import LanguageList from '@/components/shared/base/LanguageList.vue';
import { useLanguageStore } from '@/stores/language';
import { useUserStore } from '@/stores/user';
import { Language } from '@/types/common';

const { t } = useI18n();

import { getAssetImageUrl } from '@/utils/getAssetImageUrl';

declare global {
  interface Window {
    focusIframeByIndex?: (index: number) => void;
    showButtonBlockFromChild?: () => void;
  }
}

const userStore = useUserStore();
const languageStore = useLanguageStore();

const route = useRoute();

// iFrame 종합 관리 객체
const mapIframe = ref({
  0: { src: '', showIframe: false, showDropdown: false, selectedLanguage: {} },
  1: { src: '', showIframe: false, showDropdown: false, selectedLanguage: {} },
});

const refsIframe = ref<(HTMLIFrameElement | null)[]>([]); // iframe Element 목록
// 화면 추가/삭제 버튼 노출 여부
const isVisibleButtonBlock = ref(true);

// 스크린 추가 버튼 보임 여부
const isDisabledButtonAdd = computed(() => Object.keys(mapIframe.value).length > 7);

// 스크린 삭제 버튼 보임 여부
const isDisabledButtonDelete = computed(() => Object.keys(mapIframe.value).length < 3);

// 스크린 추가
const iframeAdd = () => {
  const keyIframeNew = Object.keys(mapIframe.value).length;
  mapIframe.value[keyIframeNew] = { src: '', showIframe: false, showDropdown: false, selectedLanguage: {} };
  setDefaultUrl();
};

// 스크린 삭제
const iframeDelete = () => {
  const keyIframeLast = Object.keys(mapIframe.value).length - 1;
  delete mapIframe.value[keyIframeLast];
};

const toggleDropdown = (inx: number) => {
  if (!mapIframe.value[inx]) {
    return;
  }
  mapIframe.value[inx].showDropdown = !mapIframe.value[inx].showDropdown;
};

const changeLanguage = (val: Language, inx: number) => {
  if (!mapIframe.value[inx]) {
    return;
  }
  mapIframe.value[inx].selectedLanguage = val;
  setDefaultUrl();
  toggleDropdown(inx);
};

const handleFinish = () => {
  hideButtonBlock();
  const mapIframeValues = Object.values(mapIframe.value);

  const isEveryIframeReady = mapIframeValues.every(({ src }) => !!src);
  if (!isEveryIframeReady) return;

  mapIframeValues.forEach((_, index) => {
    setUrlIframe(index);
  });
};

const onClickMove = (index: number) => {
  hideButtonBlock();
  setUrlIframe(index);
};

// iframe 주소 확정
const setUrlIframe = (index: number) => {
  mapIframe.value[index].showIframe = true;
};

const showButtonBlock = () => (isVisibleButtonBlock.value = true);
const hideButtonBlock = () => (isVisibleButtonBlock.value = false);

const handleKeydown = (event: KeyboardEvent) => {
  const isCtrl = event.ctrlKey || event.metaKey;
  const isShift = event.shiftKey;
  const code = event.code;

  if (isCtrl && isShift && code === 'Space') {
    event.preventDefault();
    showButtonBlock();
    return;
  }

  if (isCtrl && isShift && /^Digit[1-9]$/.test(code)) {
    const digit = Number(code.slice(5));
    const index = digit - 1;
    focusIframeByIndex(index);
  }

  if (isCtrl && code === 'Backspace') {
    resetAllScreenMessages();
  }
};

const handleIframeMessage = (event: MessageEvent) => {
  const eventData = event.data;
  if (!eventData || typeof eventData !== 'object') return;

  const { type, code, ctrlKey: isCtrl } = eventData;

  if (type === 'IFRAME_RESET_EVENT' && isCtrl && code === 'Backspace') {
    resetAllScreenMessages();
  }
};

const resetAllScreenMessages = () => {
  refsIframe.value.forEach((target) => {
    if (target?.contentWindow) {
      target.contentWindow.postMessage({ type: 'RESET_EVENT', code: 'Backspace', ctrlKey: true }, '*');
    }
  });
};

// 인덱스별 focus 적용
const focusIframeByIndex = (index: number) => {
  const iframeEl = refsIframe.value[index];
  if (iframeEl?.contentWindow) iframeEl.contentWindow.focus();
  else iframeEl?.focus();
};

const setDefaultUrl = () => {
  const guestCode = route.query.guestCode;
  if (!guestCode) return;
  Object.keys(mapIframe.value).forEach((key) => {
    const lang = mapIframe.value[Number(key)].selectedLanguage.code;

    mapIframe.value[Number(key)].src =
      `${window.location.origin}/chat/screen?code=${guestCode}&languageCode=${lang ? lang : userStore.pageLanguage}`;
  });
};

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
  window.addEventListener('message', handleIframeMessage);

  window.focusIframeByIndex = focusIframeByIndex;
  window.showButtonBlockFromChild = showButtonBlock;

  setDefaultUrl();
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown);
  window.removeEventListener('message', handleIframeMessage);

  delete window.focusIframeByIndex;
  delete window.showButtonBlockFromChild;
});

const isAnyIframeVisible = computed(() => Object.values(mapIframe.value).some(({ showIframe }) => showIframe));

// 현재 인덱스가 마지막이 아닌데, 다음 iframe이 showIframe 상태인지 확인
function isRightIframeVisible(currentIndex: number): boolean {
  const nextIndex = +currentIndex + 1;
  const nextIframe = mapIframe.value[nextIndex];
  return !!nextIframe?.showIframe;
}
</script>

<style scoped lang="scss">
.multi-container {
  .gb-pd {
    padding: $spacing6 $spacing6 0;
    .gray-box {
      background: $color-gray-10;
      padding: 0.75rem 1rem;
      border-radius: 0.75rem;
      p {
        font-size: $font-size-h6;
        font-weight: $font-weight-medium;
      }
    }
  }

  .container-grid {
    &.gap-6 {
      gap: 1.5rem;
    }
    display: flex;
    width: 100%;
    height: 100vh;
    .iframe-container {
      flex: 1;
      display: flex;
      flex-direction: column;
      &:last-child {
        > div {
          padding-right: 24px !important;
        }
      }
      .lang-select-btn {
        border: 1px solid $color-gray-40;
        padding: 0.75rem 1rem;
        cursor: pointer;
        svg {
          transform: rotate(180deg);
        }
      }
      .btn-pst {
        display: flex;
        justify-content: flex-end;
        button {
          width: 200px;
          height: 56px;
        }
      }
    }
  }
}

.iframe-screen {
  flex: 1;
  width: 100%; /* added */
  border: none;
}
.block-button {
  position: fixed;
  left: 50%;
  bottom: 56%;
  transform: translateX(-50%);
  z-index: 99;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 999px;
  background: $color-gray-900;
  border: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
  button {
    border: 0;
    cursor: pointer;
    height: 48px;
    min-width: 60px;
    padding: 0 1rem;
    border-radius: 999px;
    font-weight: 600;
    line-height: 1;
    // color: rgba(255, 255, 255, 0.92);
    // background: rgba(255, 255, 255, 0.1);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.1) inset,
      0 8px 18px rgba(0, 0, 0, 0.25);
    transition:
      transform 120ms ease,
      background 120ms ease,
      opacity 120ms ease,
      box-shadow 120ms ease;
    &.minus {
      background: $color-red-less-light;
      &:hover:not(:disabled) {
        background: #f34754;
      }
    }
    &.plus {
      background: $color-blue-light;
      &:hover:not(:disabled) {
        background: #347ff3;
      }
    }
    &.all-btn {
      background: $color-white;
      font-size: $font-size-p;
      font-weight: $font-weight-semibold;
      color: $color-navy-100;
      min-width: 130px;
    }
  }
}

.block-button button:hover:not(:disabled) {
  transform: translateY(-1px);
}

.block-button button:active:not(:disabled) {
  transform: translateY(0px);
}

/* Make disabled look intentionally disabled (not "everything is disabled") */
.block-button button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
  box-shadow: none;
}

.language-dropdown {
  width: 100%;
  position: relative;
  .language-options {
    position: absolute;
    width: 100%;
    z-index: 999;
    max-height: 300px;
    margin-top: 0.5rem;
    padding: 0.5rem;
    background-color: var(--surface-default);
    border-radius: 8px;
    overflow-y: auto;
    border: 1px solid $color-gray-40;
    box-shadow: $shadow-small;
  }
}
</style>
